import { Link } from 'react-router-dom'
import {ArrowRight, ShieldCheck, FileCheck2, Zap, Search, Award, Lock, Cpu, TrendingUp, Globe, CheckCircle2, ChevronRight, Star, Users, Send, ImagePlus, Link2, LayoutTemplate, BadgeCheck} from 'lucide-react'
import Layout from '@/components/Layout'

function XLogo({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return (
    <svg className={className} style={style} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
    </svg>
  )
}

// ─── Brand palette ─────────────────────────────────────────────
const C = {
  bgDeep:       '#000000',
  bgCard:       '#070a0c',
  surface:      '#05080a',

  turq:         '#1ad6c4',
  turqBright:   '#3ce8d6',
  turqGlow:     'rgba(26,214,196,0.35)',
  turqDim:      'rgba(26,214,196,0.12)',
  borderTurq:   'rgba(26,214,196,0.30)',

  border:       'rgba(255,255,255,0.07)',
  borderMed:    'rgba(255,255,255,0.12)',

  textPrimary:  '#ffffff',
  textSecond:   '#b4c0cc',
  textMuted:    '#6a7682',
  steelDim:     '#5a6672',
}

const LOGO    = 'https://static.lumi.new/9c/9c1ccefd81b9dbb855ed621cfae893f3.webp'
const HERO_BG = 'https://static.lumi.new/69/699e93a880cabd9b4a968378d1b88c64.png'

const STEPS = [
  { icon: FileCheck2, num: '01', title: 'Create Your Profile',     desc: 'Upload your banner, token logo, project description, and social links — your public-facing project page.' },
  { icon: Zap,        num: '02', title: 'Purchase an Audit',       desc: 'Pay securely and submit your verified EVM smart contract address to kick off the review.' },
  { icon: Search,     num: '03', title: 'We Audit Your Contract',  desc: 'Our team performs an in-depth security assessment covering all major EVM attack surfaces and vulnerability classes.' },
  { icon: ShieldCheck,num: '04', title: 'Earn Your Badge',         desc: 'Receive a verified audit badge, a 1–100 security score, and a full public vulnerability report.' },
]

const FEATURES = [
  { icon: Lock,       title: 'Reentrancy & Access Control',   desc: 'In-depth analysis of state changes, external calls, privileged functions, and ownership patterns.' },
  { icon: Cpu,        title: 'Multi-Chain EVM Coverage',      desc: 'Ethereum, BNB Chain, Polygon, Arbitrum, Base, Optimism and Avalanche — all supported.' },
  { icon: ShieldCheck,title: 'Transparent 1–100 Scoring',     desc: 'Every audit closes with a clear numeric score, a pass/fail verdict, and a full severity breakdown.' },
  { icon: TrendingUp, title: 'Business Logic Analysis',       desc: 'Tokenomics, mint controls, fee structures, upgrade proxies, and owner-privilege patterns reviewed.' },
  { icon: Globe,      title: 'Public Audit Registry',         desc: 'Every completed audit is published on our public registry so investors can verify your contract.' },
  { icon: Award,      title: 'Verified Audit Badge',          desc: 'Earn an embeddable "Audited by Mandox Audits" badge to display on your site and socials.' },
]

const STATS = [
  { value: 'New',  label: 'Now Accepting Audits' },
  { value: '7–10', label: 'Business Day Turnaround' },
  { value: '7',    label: 'EVM Chains Supported' },
  { value: '100%', label: 'In-Depth Audit Coverage' },
]

const TESTIMONIALS = [
  {
    quote: 'Crypto Audit Service gave us a thorough report and the public badge we needed to launch. Our community trusted us from day one.',
    author: 'Token Project Founder',
    chain: 'BNB Chain',
    stars: 5,
  },
  {
    quote: 'Fast turnaround, detailed vulnerability report, and the Mandox ecosystem promotion brought us real visibility. Absolutely worth it.',
    author: 'DeFi Protocol Team',
    chain: 'Base',
    stars: 5,
  },
  {
    quote: 'The security score and public registry listing gave our investors the confidence to commit. The audit paid for itself immediately.',
    author: 'Governance Token Project',
    chain: 'Polygon',
    stars: 5,
  },
]

const TRUST = [
  'No hidden fees — flat-rate package',
  'Report delivered within 7–10 business days',
  'Public registry with permanent audit records',
]

function TurqText({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return <span className={className} style={{ color: C.turq }}>{children}</span>
}

export default function Home() {
  return (
    <Layout>

      {/* ══════════════════════════════════════════
          HERO
      ══════════════════════════════════════════ */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
      >
        <img
          src={HERO_BG}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
          style={{ opacity: 0.6 }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(180deg, ${C.bgDeep} 0%, rgba(0,0,0,0.05) 35%, rgba(0,0,0,0.55) 72%, ${C.bgDeep} 100%)` }}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `linear-gradient(90deg, ${C.bgDeep} 0%, transparent 18%, transparent 82%, ${C.bgDeep} 100%)` }}
        />
        <div
          className="absolute bottom-0 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{ width: '70%', height: '45%', background: `radial-gradient(ellipse at 50% 100%, rgba(26,214,196,0.20) 0%, transparent 70%)`, filter: 'blur(32px)' }}
        />

        <div className="relative z-10 w-full max-w-[1060px] mx-auto px-5 py-28 flex flex-col items-center text-center">
          <img
            src={LOGO}
            alt="Crypto Audit Service"
            className="mx-auto mb-10 w-auto object-contain drop-shadow-2xl select-none"
            style={{ height: 'clamp(100px, 14vw, 180px)' }}
            draggable={false}
          />

          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase mb-8"
            style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}`, color: C.turqBright, letterSpacing: '0.1em' }}
          >
            <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0" />
            EVM Smart Contract Security
          </div>

          <h1
            className="text-white mx-auto max-w-[820px] leading-[1.04] tracking-tight"
            style={{ fontSize: 'clamp(36px, 6.5vw, 68px)', fontWeight: 300, letterSpacing: '-1.5px' }}
          >
            Audit your token.{' '}
            <TurqText>Earn trust on-chain.</TurqText>
          </h1>

          <p
            className="mt-6 leading-relaxed mx-auto max-w-[560px]"
            style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: C.textSecond, fontWeight: 300 }}
          >
            We audit EVM token smart contracts and give your project a verified badge,
            a full vulnerability report, and a transparent security score from&nbsp;1&nbsp;to&nbsp;100.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
              style={{ background: C.turq, color: '#000000', boxShadow: `0 0 36px rgba(26,214,196,0.40), 0 2px 12px rgba(0,0,0,0.5)` }}
            >
              Get Audited <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-medium transition-all hover:bg-white/5 active:scale-95"
              style={{ color: C.textPrimary, border: `1px solid ${C.borderMed}`, background: 'rgba(255,255,255,0.03)' }}
            >
              Explore Audits <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="mt-12 flex flex-wrap justify-center gap-3">
            {TRUST.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full"
                style={{ color: C.textSecond, background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}` }}
              >
                <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: C.turq }} />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          STATS BAR
      ══════════════════════════════════════════ */}
      <section style={{ background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-[1060px] mx-auto px-5 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map((s) => (
            <div key={s.label} className="flex flex-col items-center gap-1">
              <p className="text-4xl font-light" style={{ color: C.turq, letterSpacing: '-1px' }}>
                {s.value}
              </p>
              <p className="text-xs tracking-wide uppercase" style={{ color: C.steelDim, letterSpacing: '0.08em' }}>
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════
          HOW IT WORKS
      ══════════════════════════════════════════ */}
      <section style={{ background: C.bgDeep }} className="py-28">
        <div className="max-w-[1060px] mx-auto px-5">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase mb-4 inline-block" style={{ color: C.turq, letterSpacing: '0.12em' }}>
              The Process
            </span>
            <h2 className="text-white font-light leading-tight" style={{ fontSize: 'clamp(26px, 4vw, 42px)', letterSpacing: '-0.8px' }}>
              From submission to <TurqText>verified badge</TurqText>
            </h2>
            <p className="mt-3 text-sm max-w-md mx-auto" style={{ color: C.textMuted }}>
              Four clear steps from contract submission to a published, investor-visible audit record.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s, i) => (
              <div
                key={s.title}
                className="relative rounded-xl p-6 flex flex-col gap-4 group"
                style={{ background: C.bgCard, border: `1px solid ${C.border}`, transition: 'border-color 0.2s, box-shadow 0.2s' }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = C.borderTurq
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 28px ${C.turq}22`
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = C.border
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'none'
                }}
              >
                <span
                  className="absolute top-4 right-5 font-black leading-none select-none pointer-events-none"
                  style={{ fontSize: '3.5rem', color: 'rgba(255,255,255,0.025)', letterSpacing: '-2px' }}
                >
                  {s.num}
                </span>

                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                >
                  <s.icon className="w-5 h-5" style={{ color: C.turq }} />
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: C.turq, letterSpacing: '0.1em' }}>
                    Step {s.num}
                  </p>
                  <h3 className="text-base font-semibold text-white mb-2 leading-snug">{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>{s.desc}</p>
                </div>

                {i < STEPS.length - 1 && (
                  <div
                    className="hidden lg:block absolute top-[2.8rem] right-0 w-5 h-px"
                    style={{ background: `linear-gradient(90deg, ${C.turq}55, transparent)`, transform: 'translateX(100%)' }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          FEATURES GRID
      ══════════════════════════════════════════ */}
      <section
        style={{ background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}
        className="py-28"
      >
        <div className="max-w-[1060px] mx-auto px-5">
          <div className="text-center mb-16">
            <span className="text-xs font-semibold tracking-widest uppercase mb-4 inline-block" style={{ color: C.turq, letterSpacing: '0.12em' }}>
              Audit Coverage
            </span>
            <h2 className="text-white font-light leading-tight" style={{ fontSize: 'clamp(26px, 4vw, 42px)', letterSpacing: '-0.8px' }}>
              What we <TurqText>check</TurqText>
            </h2>
            <p className="mt-3 text-sm max-w-md mx-auto" style={{ color: C.textMuted }}>
              Comprehensive coverage across every attack surface in your smart contract.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-xl p-6 group"
                style={{ background: C.bgCard, border: `1px solid ${C.border}`, transition: 'border-color 0.2s, box-shadow 0.2s' }}
                onMouseEnter={(e) => {
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = C.borderTurq
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = `0 0 24px ${C.turq}1a`
                }}
                onMouseLeave={(e) => {
                  ;(e.currentTarget as HTMLDivElement).style.borderColor = C.border
                  ;(e.currentTarget as HTMLDivElement).style.boxShadow = 'none'
                }}
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center mb-5"
                  style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                >
                  <f.icon className="w-5 h-5" style={{ color: C.turq }} />
                </div>
                <h3 className="text-sm font-semibold text-white mb-2 leading-snug">{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CUSTOMIZABLE PROFILE
      ══════════════════════════════════════════ */}
      <section style={{ background: C.bgDeep }} className="py-28">
        <div className="max-w-[1060px] mx-auto px-5">
          <div className="flex flex-col lg:flex-row items-center gap-16">

            {/* Left: copy */}
            <div className="flex-1 text-center lg:text-left">
              <span className="text-xs font-semibold tracking-widest uppercase mb-4 inline-block" style={{ color: C.turq, letterSpacing: '0.12em' }}>
                Your Profile
              </span>
              <h2 className="text-white font-light leading-tight mb-5" style={{ fontSize: 'clamp(26px, 4vw, 42px)', letterSpacing: '-0.8px' }}>
                A public presence <TurqText>built around your brand</TurqText>
              </h2>
              <p className="text-sm leading-relaxed mb-8 max-w-[480px]" style={{ color: C.textSecond }}>
                Every token project on Mandox Audits gets a fully customisable public profile page.
                Upload your banner, set your logo, write your project story, and link your socials —
                all in one place that investors and your community can discover.
              </p>
              <ul className="flex flex-col gap-3 mb-10">
                {[
                  'Custom banner image & token logo upload',
                  'Project description & extended bio',
                  'Social links: website, X, Telegram, Discord, GitHub',
                  'Live audit badge, security score & vulnerability report',
                  'Shareable social media card & downloadable PDF report',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2.5 text-sm" style={{ color: C.textSecond }}>
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: C.turq }} />
                    {item}
                  </li>
                ))}
              </ul>
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
                style={{ background: C.turq, color: '#000000', boxShadow: `0 0 28px rgba(26,214,196,0.30)` }}
              >
                Create Your Profile <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Right: mock profile card */}
            <div className="flex-shrink-0 w-full max-w-[400px]">
              <div
                className="rounded-2xl overflow-hidden"
                style={{ border: `1px solid ${C.borderTurq}`, boxShadow: `0 0 60px rgba(26,214,196,0.12), 0 8px 32px rgba(0,0,0,0.6)` }}
              >
                {/* Mock banner */}
                <div
                  className="relative h-28 flex items-end px-5 pb-3"
                  style={{ background: `linear-gradient(135deg, #0a1a1f 0%, #0d2a2e 50%, #071410 100%)` }}
                >
                  <div
                    className="absolute inset-0 opacity-20"
                    style={{ backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 24px, ${C.turq}18 24px, ${C.turq}18 25px), repeating-linear-gradient(90deg, transparent, transparent 24px, ${C.turq}18 24px, ${C.turq}18 25px)` }}
                  />
                  <div className="flex items-center gap-2 relative z-10">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ background: C.turq, boxShadow: `0 0 8px ${C.turq}` }}
                    />
                    <span className="text-xs font-semibold" style={{ color: C.turq }}>Live Preview</span>
                  </div>
                  {/* Mock avatar */}
                  <div
                    className="absolute -bottom-6 left-5 w-14 h-14 rounded-xl flex items-center justify-center text-lg font-black"
                    style={{ background: `linear-gradient(135deg, ${C.turq}, #0d9488)`, border: `3px solid ${C.bgCard}`, color: '#000', boxShadow: `0 0 20px ${C.turq}44` }}
                  >
                    M
                  </div>
                </div>

                {/* Mock card body */}
                <div className="px-5 pt-10 pb-5" style={{ background: C.bgCard }}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <p className="font-semibold text-white text-sm">My Token Project</p>
                      <p className="text-xs" style={{ color: C.textMuted }}>$TOKEN · Ethereum</p>
                    </div>
                    <span
                      className="text-xs px-2.5 py-1 rounded-full font-semibold flex-shrink-0"
                      style={{ background: 'rgba(34,197,94,0.12)', color: '#4ade80', border: '1px solid rgba(34,197,94,0.25)' }}
                    >
                      ✓ Audited
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed mb-4" style={{ color: C.textMuted }}>
                    A next-generation DeFi protocol with a focus on community governance and transparent tokenomics.
                  </p>

                  {/* Mock profile feature icons */}
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {[
                      { icon: ImagePlus,      label: 'Banner' },
                      { icon: LayoutTemplate, label: 'Profile' },
                      { icon: Link2,          label: 'Socials' },
                      { icon: BadgeCheck,     label: 'Badge' },
                    ].map(({ icon: Icon, label }) => (
                      <div key={label} className="flex flex-col items-center gap-1 py-2 rounded-lg" style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: C.turq }} />
                        <span className="text-xs" style={{ color: C.turq }}>{label}</span>
                      </div>
                    ))}
                  </div>

                  {/* Mock social pills */}
                  <div className="flex flex-wrap gap-1.5">
                    {['Website', 'X / Twitter', 'Telegram'].map((s) => (
                      <span key={s} className="text-xs px-2.5 py-1 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: C.textMuted }}>
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          REGISTRY CALLOUT
      ══════════════════════════════════════════ */}
      <section style={{ background: C.bgDeep }} className="py-24">
        <div className="max-w-[1060px] mx-auto px-5">
          <div className="flex items-end justify-between mb-12 gap-4 flex-wrap">
            <div>
              <span className="text-xs font-semibold tracking-widest uppercase mb-3 inline-block" style={{ color: C.turq, letterSpacing: '0.12em' }}>
                Live Registry
              </span>
              <h2 className="text-white font-light leading-tight" style={{ fontSize: 'clamp(24px, 3.5vw, 38px)', letterSpacing: '-0.8px' }}>
                Public <TurqText>audit registry</TurqText>
              </h2>
              <p className="mt-2 text-sm max-w-lg" style={{ color: C.textMuted }}>
                Every completed audit is published permanently — visible to investors, communities, and token holders.
              </p>
            </div>
            <Link
              to="/projects"
              className="inline-flex items-center gap-1.5 text-sm transition-colors hover:text-white flex-shrink-0"
              style={{ color: C.turq }}
            >
              View all projects <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: ShieldCheck, label: 'Verified Badge',  desc: 'A public badge on your project profile confirming the audit passed.' },
              { icon: TrendingUp,  label: 'Security Score',  desc: 'A transparent 1–100 score summarising your contract overall risk level.' },
              { icon: Award,       label: 'Full Report',     desc: 'Every finding, severity, and resolution published in your public audit record.' },
            ].map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="rounded-xl p-6 flex flex-col gap-4"
                style={{ background: C.bgCard, border: `1px solid ${C.border}` }}
              >
                <div
                  className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                >
                  <Icon className="w-5 h-5" style={{ color: C.turq }} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">{label}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: C.textMuted }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
              style={{ background: C.turq, color: '#000000', boxShadow: `0 0 28px rgba(26,214,196,0.30)` }}
            >
              Be the first audited project <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════ */}
      <section style={{ background: C.surface, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }} className="py-24">
        <div className="max-w-[1060px] mx-auto px-5">
          <div className="text-center mb-14">
            <span className="text-xs font-semibold tracking-widest uppercase mb-4 inline-block" style={{ color: C.turq, letterSpacing: '0.12em' }}>
              What Clients Say
            </span>
            <h2 className="text-white font-light leading-tight" style={{ fontSize: 'clamp(24px, 3.5vw, 38px)', letterSpacing: '-0.8px' }}>
              Trusted by <TurqText>token projects</TurqText>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="rounded-xl p-6 flex flex-col gap-4"
                style={{ background: C.bgCard, border: `1px solid ${C.border}` }}
              >
                {/* Stars */}
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: t.stars }).map((_, si) => (
                    <Star key={si} className="w-3.5 h-3.5 fill-current" style={{ color: C.turq }} />
                  ))}
                </div>
                <p className="text-sm leading-relaxed flex-1 italic" style={{ color: C.textSecond }}>
                  "{t.quote}"
                </p>
                <div className="flex items-center gap-2 pt-1 border-t" style={{ borderColor: C.border }}>
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}` }}
                  >
                    <Users className="w-3.5 h-3.5" style={{ color: C.turq }} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-white">{t.author}</p>
                    <p className="text-xs" style={{ color: C.textMuted }}>{t.chain}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          COMMUNITY / SOCIAL BAND
      ══════════════════════════════════════════ */}
      <section style={{ background: C.bgDeep }} className="py-20">
        <div className="max-w-[760px] mx-auto px-5 text-center">
          <span className="text-xs font-semibold tracking-widest uppercase mb-5 inline-block" style={{ color: C.turq, letterSpacing: '0.12em' }}>
            Community
          </span>
          <h2 className="text-white font-light mb-4 leading-tight" style={{ fontSize: 'clamp(24px, 3.5vw, 38px)', letterSpacing: '-0.8px' }}>
            Join the <TurqText>Mandox ecosystem</TurqText>
          </h2>
          <p className="text-sm leading-relaxed mb-10 max-w-md mx-auto" style={{ color: C.textSecond }}>
            Stay up to date on new audits, security tips, and ecosystem news. Every completed audit gets promoted to our 30,000+ followers.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a
              href="https://x.com/Mandox_Audits"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
              style={{ background: C.turq, color: '#000000', boxShadow: `0 0 28px rgba(26,214,196,0.30)` }}
            >
              <XLogo className="w-4 h-4" />
              Follow @Mandox_Audits
            </a>
            <a
              href="https://t.me/officialmandox"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 px-6 py-3 rounded-xl text-sm font-medium transition-all hover:bg-white/5 active:scale-95"
              style={{ color: C.textPrimary, border: `1px solid ${C.borderMed}`, background: 'rgba(255,255,255,0.03)' }}
            >
              <Send className="w-4 h-4" />
              Join Telegram
            </a>
          </div>
          <div className="mt-8 inline-flex items-center gap-2 text-xs px-4 py-2 rounded-full" style={{ background: C.turqDim, border: `1px solid ${C.borderTurq}`, color: C.turq }}>
            <Award className="w-3.5 h-3.5" />
            30,000+ combined followers across Mandox ecosystem pages
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════
          CTA BAND
      ══════════════════════════════════════════ */}
      <section className="relative overflow-hidden py-28" style={{ background: C.surface }}>
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: `radial-gradient(ellipse at 50% 50%, rgba(26,214,196,0.10) 0%, transparent 65%)` }}
        />
        <div className="absolute left-0 right-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${C.turq}55, transparent)` }} />
        <div className="absolute left-0 right-0 bottom-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${C.turq}55, transparent)` }} />

        <div className="relative max-w-[680px] mx-auto px-5 text-center">
          <span className="text-xs font-semibold tracking-widest uppercase mb-5 inline-block" style={{ color: C.turq, letterSpacing: '0.12em' }}>
            Ready to get started?
          </span>
          <h2 className="text-white font-light mb-5 leading-tight" style={{ fontSize: 'clamp(28px, 4.5vw, 46px)', letterSpacing: '-0.8px' }}>
            Secure your contract.<br />
            <TurqText>Build investor confidence.</TurqText>
          </h2>
          <p className="text-base leading-relaxed mb-10" style={{ color: C.textSecond, fontWeight: 300 }}>
            Get a verified badge, a 1–100 security score, and a full vulnerability report — promoted across the Mandox ecosystem.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-md px-8 py-4 text-sm font-semibold transition-all hover:brightness-110 active:scale-95"
              style={{ background: C.turq, color: '#000000', boxShadow: `0 0 42px rgba(26,214,196,0.35), 0 2px 16px rgba(0,0,0,0.5)` }}
            >
              View Audit Package <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/projects"
              className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-medium transition-all hover:bg-white/5"
              style={{ color: C.textSecond, border: `1px solid ${C.borderMed}`, background: 'rgba(255,255,255,0.03)' }}
            >
              Browse Projects
            </Link>
          </div>
        </div>
      </section>

    </Layout>
  )
}
